import { useState } from 'react'
import { Button, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { TransactionCategoryDropdown } from '@/components/TransactionCategoryDropdown'
import { useConfirmCategory } from '../../hooks/useConfirmCategory'
import { BookkeeperHandOff } from './BookkeeperHandOff'
import { QuestionHeading } from './cardParts'
import { WidgetCard } from './WidgetCard'
import type { WidgetProps } from './types'

type Stage = 'confirm' | 'handOff'

interface HandOffStageProps {
  question: string
  disabled: boolean
  saving: boolean
  onSend: (note: string) => void
  onBack: () => void
}

const HandOffStage = ({
  question,
  disabled,
  saving,
  onSend,
  onBack,
}: HandOffStageProps) => (
  <WidgetCard>
    <QuestionHeading>{question}</QuestionHeading>

    <BookkeeperHandOff
      disabled={disabled}
      saving={saving}
      onSend={onSend}
      onBack={onBack}
    />
  </WidgetCard>
)

export const ConfirmTransactionCategoryWidget = ({
  input,
  resolved,
  outcome,
  onOutcome,
}: WidgetProps) => {
  const groupKey = typeof input.groupKey === 'string' ? input.groupKey : ''
  const [stage, setStage] = useState<Stage>('confirm')
  const {
    group,
    groupCategoryId,
    nameOf,
    setGroupCategory,
    confirm,
    handOff,
    markPersonal,
    saving,
  } = useConfirmCategory(groupKey, onOutcome)

  if (!group || outcome !== undefined) return null

  const category = nameOf(groupCategoryId)
  const confirmLabel = category
    ? `Go with ${category}`
    : 'Go with this category'
  const question = category
    ? `Does ${category} sound right for ${group.merchant}?`
    : `Which category fits ${group.merchant}?`

  const send = async (note: string) => {
    if (await handOff(note)) setStage('confirm')
  }

  if (stage === 'handOff') {
    return (
      <HandOffStage
        question={question}
        disabled={resolved}
        saving={saving}
        onSend={(note) => void send(note)}
        onBack={() => setStage('confirm')}
      />
    )
  }

  return (
    <WidgetCard>
      <QuestionHeading>{question}</QuestionHeading>

      <div className="flex flex-col gap-1">
        <Text variant={TypographyStyles.LABEL_SM} color={TextColors.HEADER}>
          Category
        </Text>
        <TransactionCategoryDropdown
          optionStyle="normal"
          className="body-sm"
          buttonWidthClassName="w-full min-w-0"
          value={groupCategoryId ?? undefined}
          transactionAmount={group.totalInCents}
          transactionDate={group.transactions[0]?.isoDate}
          disabled={resolved || saving}
          onChange={setGroupCategory}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          variant="primary"
          className="max-w-full"
          title={confirmLabel}
          disabled={resolved || groupCategoryId === null}
          loading={saving}
          onClick={confirm}
        >
          {/* A span rather than Text, which renders a <p> and is invalid inside a button. */}
          <span className="min-w-0 truncate">{confirmLabel}</span>
        </Button>
        <Button
          size="sm"
          variant="secondary"
          className="shrink-0"
          disabled={resolved || saving}
          onClick={() => void markPersonal()}
        >
          Personal
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="shrink-0"
          disabled={resolved || saving}
          onClick={() => setStage('handOff')}
        >
          Ask a bookkeeper
        </Button>
      </div>
    </WidgetCard>
  )
}
